import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import Navigation from '../components/Navigation';
import { mainContentClassName } from '../context/NavContext';
import { getImageUrl } from '../utils/imageUtils';
import { useBackgroundFix } from '../utils/backgroundFix';

function GroceryList() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));
  const [groceryList, setGroceryList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [hoveredIngredient, setHoveredIngredient] = useState(null);
  const [clickedIngredient, setClickedIngredient] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const ingredientRefs = useRef({});
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeDetails, setRecipeDetails] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);

  // Fix background height on mobile
  useBackgroundFix('background-fixed', [groceryList]);

  // Auto-fetch grocery list when dates change (skip initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (startDate && endDate && new Date(startDate) <= new Date(endDate)) {
      handleFetchGroceryList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Close tooltip when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clickedIngredient && tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        // Check if click is on the ingredient text itself
        const ingredientElement = ingredientRefs.current[clickedIngredient];
        if (ingredientElement && !ingredientElement.contains(event.target)) {
          setClickedIngredient(null);
        }
      }
    };

    if (clickedIngredient) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [clickedIngredient]);

  const handleFetchGroceryList = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/grocery-list`, {
        params: {
          startDate,
          endDate
        }
      });
      setGroceryList(response.data);
    } catch (error) {
      console.error('Error fetching grocery list:', error);
      alert('Failed to fetch grocery list');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = (ingredientName) => {
    setCheckedItems(prev => ({
      ...prev,
      [ingredientName]: !prev[ingredientName]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!groceryList || groceryList.groceries.length === 0) {
      alert('No grocery list to export');
      return;
    }

    // Filter out checked/removed items
    const itemsToExport = groceryList.groceries.filter(item => !checkedItems[item.name]);

    // Create text content
    let textContent = `GROCERY LIST\n`;
    textContent += `Date Range: ${moment(startDate).format('MMM D, YYYY')} - ${moment(endDate).format('MMM D, YYYY')}\n`;
    textContent += `Meals to shop for: ${groceryList.totalMeals}\n`;
    if (groceryList.leftoverMealsExcluded > 0) {
      textContent += `Leftover meals excluded: ${groceryList.leftoverMealsExcluded}\n`;
    }
    textContent += `\n${'='.repeat(50)}\n\n`;

    if (itemsToExport.length === 0) {
      textContent += 'No items to export (all items have been checked off).\n';
    } else {
      textContent += 'INGREDIENTS:\n\n';
      itemsToExport.forEach((item, index) => {
        textContent += `${index + 1}. ${item.name} - ${item.quantity} ${item.unit}\n`;
      });
    }

    // Create a blob and download
    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `grocery-list-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRecipeClick = async (recipeId) => {
    // Only show popup on non-mobile devices
    if (window.innerWidth < 768) {
      return;
    }

    if (selectedRecipe === recipeId && recipeDetails) {
      // If clicking the same recipe, close the modal
      setSelectedRecipe(null);
      setRecipeDetails(null);
      return;
    }

    setSelectedRecipe(recipeId);
    setLoadingRecipe(true);

    try {
      const response = await axios.get(`/api/recipes/${recipeId}`);
      setRecipeDetails(response.data);
    } catch (error) {
      console.error('Error fetching recipe details:', error);
      alert('Failed to load recipe details');
      setSelectedRecipe(null);
    } finally {
      setLoadingRecipe(false);
    }
  };

  return (
    <>
      <div 
        id="background-fixed"
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/edit-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1
        }}
      />
      <style>{`
        @media (max-width: 767px) {
          #background-fixed {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            width: 100vw;
            min-height: 100vh;
            background-attachment: fixed;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
          }
        }
      `}</style>
      <Navigation />
      <div className={mainContentClassName}>
        <div className="max-w-6xl mx-auto">
        <div className="mb-4 sm:mb-6 flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Grocery List</h1>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">Select Date Range</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded"
              />
            </div>
          </div>
          <button
            onClick={handleFetchGroceryList}
            disabled={loading}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md"
          >
            {loading ? 'Loading...' : 'Generate Grocery List'}
          </button>
        </div>

        {groceryList && (
          <>
            <div className="flex justify-between items-center mb-6 print:hidden bg-white rounded-lg shadow-lg p-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">Shopping List</h2>
                <p className="text-gray-600">
                  {moment(startDate).format('MMM D')} - {moment(endDate).format('MMM D, YYYY')}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {groceryList.leftoverMealsExcluded > 0
                    ? `${groceryList.totalMeals} meals to shop for (${groceryList.leftoverMealsExcluded} leftover meals excluded)`
                    : `${groceryList.totalMeals} meals planned`}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleExport}
                  className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-md"
                >
                  Export List
                </button>
                <button
                  onClick={handlePrint}
                  className="px-6 py-3 bg-sky-600 text-white rounded-lg font-semibold hover:bg-sky-700 transition-colors shadow-md"
                >
                  Print List
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Side - Shopping List */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="space-y-2">
                  {groceryList.groceries.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      No ingredients found for the selected date range.
                    </p>
                  ) : (
                    groceryList.groceries.map((item, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-3 border rounded-lg transition-all grocery-item ${
                            checkedItems[item.name] ? 'bg-gray-100 line-through text-gray-500 removed-item' : 'bg-white'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checkedItems[item.name] || false}
                            onChange={() => handleToggleItem(item.name)}
                            className="w-5 h-5 text-orange-500 rounded cursor-pointer print:hidden"
                          />
                          <div className="flex-1">
                            <span className="font-semibold text-sm">{item.name}</span>
                            <span className="ml-2 text-sm text-gray-600">
                              {item.quantity} {item.unit}
                            </span>
                          </div>
                          <div 
                            ref={(el) => { ingredientRefs.current[item.name] = el; }}
                            className="text-xs text-gray-500 print:hidden relative cursor-pointer touch-manipulation"
                            onMouseEnter={(e) => {
                              // Only use hover on non-touch devices
                              if (!('ontouchstart' in window)) {
                                setHoveredIngredient(item.name);
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPosition({
                                  x: rect.right + 10,
                                  y: rect.top + (rect.height / 2)
                                });
                              }
                            }}
                            onMouseLeave={() => {
                              if (!('ontouchstart' in window)) {
                                setHoveredIngredient(null);
                              }
                            }}
                            onClick={(e) => {
                              // Handle click for mobile/touch devices
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              if (clickedIngredient === item.name) {
                                // Close if already open
                                setClickedIngredient(null);
                              } else {
                                // Open tooltip
                                setClickedIngredient(item.name);
                                // Position tooltip - on mobile, center it or position below
                                const isMobile = window.innerWidth < 768;
                                setTooltipPosition({
                                  x: isMobile ? window.innerWidth / 2 : rect.right + 10,
                                  y: isMobile ? rect.bottom + 10 : rect.top + (rect.height / 2)
                                });
                              }
                            }}
                          >
                            {item.recipes.length} {item.recipes.length === 1 ? 'recipe' : 'recipes'}
                            {(hoveredIngredient === item.name || clickedIngredient === item.name) && (
                              <div
                                ref={tooltipRef}
                                className="fixed z-50 bg-gray-800 text-white text-xs rounded-lg shadow-xl p-3 min-w-[200px] max-w-[300px]"
                                style={{
                                  left: window.innerWidth < 768 ? '50%' : `${tooltipPosition.x}px`,
                                  top: window.innerWidth < 768 ? `${tooltipPosition.y}px` : `${tooltipPosition.y}px`,
                                  transform: window.innerWidth < 768 ? 'translateX(-50%)' : 'translateY(-50%)',
                                  pointerEvents: 'auto',
                                  maxWidth: window.innerWidth < 768 ? '90vw' : '300px'
                                }}
                                onMouseEnter={() => {
                                  if (!('ontouchstart' in window)) {
                                    setHoveredIngredient(item.name);
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (!('ontouchstart' in window)) {
                                    setHoveredIngredient(null);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="font-semibold mb-2 pb-2 border-b border-gray-600">
                                  Used in:
                                </div>
                                <div className="space-y-1">
                                  {item.recipes.map((recipe, idx) => (
                                    <div key={idx} className="flex items-start gap-2">
                                      <span className="text-orange-400 capitalize text-xs whitespace-nowrap">
                                        {recipe.mealType}:
                                      </span>
                                      <span className="flex-1 text-xs">
                                        {recipe.recipeTitle}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>

              {/* Right Side - Recipe Breakdown */}
              {groceryList.groceries.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h3 className="text-xl font-bold mb-4">Recipe Breakdown</h3>
                  <div className="space-y-4 max-h-[600px] overflow-y-auto print:max-h-none print:overflow-visible">
                    {Object.entries(
                      groceryList.groceries.reduce((acc, item) => {
                        item.recipes.forEach(recipe => {
                          const key = `${recipe.date}-${recipe.recipeId}`;
                          if (!acc[key]) {
                            acc[key] = {
                              date: recipe.date,
                              recipeTitle: recipe.recipeTitle,
                              mealType: recipe.mealType,
                              recipeId: recipe.recipeId,
                              ingredients: []
                            };
                          }
                          acc[key].ingredients.push(item.name);
                        });
                        return acc;
                      }, {})
                    ).map(([key, info]) => {
                      const recipeId = info.recipeId;
                      
                      return (
                        <div key={key} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="font-semibold capitalize">{info.mealType}</span>
                              <span className="text-gray-600 ml-2">
                                {moment(info.date).format('MMM D, YYYY')}
                              </span>
                            </div>
                          </div>
                          <p 
                            className={`text-gray-800 font-medium ${recipeId ? 'cursor-pointer hover:text-amber-600 transition-colors hidden md:block' : ''}`}
                            onClick={() => recipeId && handleRecipeClick(recipeId)}
                            title={recipeId ? 'Click to view recipe details' : ''}
                          >
                            {info.recipeTitle}
                          </p>
                          <p className="text-gray-800 font-medium md:hidden">
                            {info.recipeTitle}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Recipe Details Modal - Desktop Only */}
        {selectedRecipe && (
          <div 
            className="hidden md:flex fixed inset-0 bg-black bg-opacity-50 items-center justify-center z-50 p-4"
            onClick={() => {
              setSelectedRecipe(null);
              setRecipeDetails(null);
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {loadingRecipe ? (
                <div className="p-8 text-center">
                  <p className="text-gray-600">Loading recipe details...</p>
                </div>
              ) : recipeDetails ? (
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">{recipeDetails.title}</h2>
                    <button
                      onClick={() => {
                        setSelectedRecipe(null);
                        setRecipeDetails(null);
                      }}
                      className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                    >
                      ×
                    </button>
                  </div>

                  {recipeDetails.imageUrl && (
                    <img
                      src={getImageUrl(recipeDetails.imageUrl)}
                      alt={recipeDetails.title}
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                  )}

                  <div className="grid grid-cols-2 gap-4 mb-6 pb-4 border-b">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-500">
                        {recipeDetails.totalTime || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-600">Duration (min)</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-orange-500">
                        {recipeDetails.servings}
                      </p>
                      <p className="text-sm text-gray-600">Servings</p>
                    </div>
                  </div>

                  {recipeDetails.description && (
                    <p className="text-gray-600 mb-6">{recipeDetails.description}</p>
                  )}

                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-3">Ingredients</h3>
                    <ul className="space-y-2">
                      {recipeDetails.ingredients && recipeDetails.ingredients.map((ingredient, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-orange-500">•</span>
                          <span className="font-semibold">{ingredient.quantity} {ingredient.unit}</span>
                          <span>{ingredient.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/recipes/${selectedRecipe}`)}
                      className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-md"
                    >
                      View Full Recipe
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRecipe(null);
                        setRecipeDetails(null);
                      }}
                      className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors shadow-md"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <p className="text-gray-600">Failed to load recipe details</p>
                  <button
                    onClick={() => {
                      setSelectedRecipe(null);
                      setRecipeDetails(null);
                    }}
                    className="mt-4 px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      <style>{`
        @media print {
          button, input[type="checkbox"], .print\\:hidden {
            display: none;
          }
          .bg-gray-50 {
            background-color: white;
          }
          /* Hide checked/removed items in print view */
          .removed-item {
            display: none;
          }
        }
      `}</style>
    </>
  );
}

export default GroceryList;

